import React from 'react'
import {TypedElement} from '../../../entities/project'
import {Transformer as TransformerRef} from 'konva/lib/shapes/Transformer'
import {KonvaEventObject, NodeConfig, Node as NodeRef} from 'konva/lib/Node'
import {TransformerManager} from '../transformerLayers/TransformerManager'
import {canvasElementsRegistry} from '../elements/CanvasElementsRegistry'
import useImage from 'use-image'
import {instance as elementTypeService} from '../../../services/ElementTypeService'
import {Plant} from '../../../entities/plant'
import {KonvaPointerEvent} from 'konva/lib/PointerEvents'

export type CanvasEditorPlantProps = {
  plant: Plant
  isSelected: boolean
  onSelect: (evt: KonvaPointerEvent) => void
  onChange?: (modified: TypedElement) => void
  onTwiceClick: () => void
  isEditable: boolean
  isSelectable: boolean
  setCursor: (cursor: string) => void
  resetCursor: () => void
  rotatable: boolean
  specimenIndex: number
}

export function CanvasEditorPlant(props: CanvasEditorPlantProps) {
  const shapeRef = React.useRef<NodeRef>()
  const transformerRef = React.useRef<TransformerRef>()

  React.useEffect(() => {
    if (props.isSelected) {
      // we need to attach transformer manually
      transformerRef.current?.nodes([shapeRef.current])
      transformerRef.current?.getLayer().batchDraw()
    }
  }, [props.isSelected])

  React.useEffect(() => {
    const handler = () => props.onTwiceClick()
    shapeRef.current?.on('dblclick dbltap', handler)

    return () => {
      if (shapeRef.current) {
        shapeRef.current.off('dblclick dbltap', handler)
      }
    }
  }, [props.onTwiceClick])

  React.useEffect(() => {
    console.log(
      `rendering coordinates for plant ${props.plant.id}: [${props.plant.coordinates
        .map((c) => `(x:${c.x}, y:${c.y}, w:${c.width}, h:${c.height})`)
        .join(' ')}]`
    )
  }, [props.plant.coordinates])

  const canvasShapeType = canvasElementsRegistry['plant']?.canvasShapeType

  if (!canvasShapeType) {
    return null
  }

  const elementInfo = elementTypeService.getElementType('plant')

  if (!elementInfo) {
    return null
  }

  const displayProps = {
    image:
      elementInfo.templates[
        elementTypeService.chooseElementTypeTemplate('plant', props.plant.template)
      ].src,
  }

  let [templateImage] = useImage(displayProps?.image)

  const elementProps = props.plant.coordinates[props.specimenIndex || 0]

  const plantAsElement = {
    elementId: props.plant.id,
    name: props.plant.name,
    shapeType: 'plant',
    props: elementProps,
    type: 'plant',
  } as TypedElement

  return (
    <>
      {React.createElement(canvasShapeType, {
        ...elementProps,
        draggable: props.isEditable && props.isSelected,
        ...displayProps,
        image: templateImage,
        onClick:
          props.isEditable || props.isSelectable
            ? (evt) => {
                props.onSelect(evt)
                props.setCursor('move')
              }
            : undefined,
        onDoubleClick: props.isEditable ? props.onTwiceClick : undefined,
        ref: (element: NodeRef) => {
          if (!element) {
            return
          }
          element['_plantId'] = props.plant.id
          element['_specimenIndex'] = props.specimenIndex
          shapeRef.current = element
        },
        // zIndex: /*props.isSelected && props.isEditable ? 100 :*/ elementInfo.zIndex,
        onMouseEnter:
          props.isEditable || props.isSelectable
            ? () => {
                if (props.isSelected) {
                  props.setCursor('move')
                } else {
                  props.setCursor('pointer')
                }
              }
            : undefined,
      })}

      {props.isSelected && props.isEditable && (
        <TransformerManager
          element={plantAsElement}
          mode="edit"
          transformerRef={transformerRef}
          shapeRef={shapeRef}
          onChange={props.onChange}
          // transformerLayerProps={props.transformerLayerProps}
        ></TransformerManager>
      )}

      {props.isSelected && !props.isEditable && props.isSelectable && (
        <TransformerManager
          element={plantAsElement}
          mode="selection"
          transformerRef={transformerRef}
          shapeRef={shapeRef}
          onChange={props.onChange}
        ></TransformerManager>
      )}
    </>
  )
}
